// AI API Helper - Simplified for Cloud Functions
// API keys are now stored securely in Firebase Cloud Functions
// This file handles provider preferences and settings only

import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { db, appId } from '../config/firebase';

// Cache untuk global settings
let globalSettingsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 menit
let settingsListenerInitialized = false;
let settingsUnsubscribe = null;

// Available AI providers
export const AI_PROVIDERS = [
    { id: 'groq', name: 'Groq', defaultModel: 'openai/gpt-oss-20b' },
    { id: 'gemini', name: 'Google Gemini', defaultModel: 'gemini-3-flash-preview' },
    { id: 'deepseek', name: 'DeepSeek (V3)', defaultModel: 'deepseek-chat' },
    { id: 'comet', name: 'Comet (DeepSeek V3.2)', defaultModel: 'deepseek-v3.2-exp' },
    { id: 'grok', name: 'xAI Grok', defaultModel: 'grok-3-mini-beta' }
];

/**
 * Load global settings dari Firebase dengan caching
 */
export const loadGlobalSettings = async () => {
    const now = Date.now();

    // Inisialisasi realtime listener satu kali untuk memutakhirkan cache otomatis
    if (!settingsListenerInitialized) {
        try {
            const settingsRef = doc(db, `artifacts/${appId}/settings/global`);
            settingsUnsubscribe = onSnapshot(settingsRef, (snapshot) => {
                if (snapshot.exists()) {
                    globalSettingsCache = snapshot.data();
                    cacheTimestamp = Date.now();
                    console.log('🔔 Global settings updated via realtime listener');
                }
            }, (error) => {
                console.error('Realtime listener error for global settings:', error);
            });
            settingsListenerInitialized = true;
        } catch (e) {
            console.error('Failed to init realtime listener for global settings:', e);
        }
    }

    // Return cached data jika masih valid
    if (globalSettingsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
        return globalSettingsCache;
    }

    try {
        const settingsRef = doc(db, `artifacts/${appId}/settings/global`);
        const settingsSnap = await getDoc(settingsRef);

        if (settingsSnap.exists()) {
            globalSettingsCache = settingsSnap.data();
            cacheTimestamp = now;
            return globalSettingsCache;
        }

        return null;
    } catch (error) {
        console.error('Error loading global settings:', error);
        return null;
    }
};

/**
 * Get default AI provider dari global settings
 */
export const getDefaultAIProvider = async () => {
    try {
        const globalSettings = await loadGlobalSettings();
        return globalSettings?.aiSettings?.defaultProvider || 'grok';
    } catch (error) {
        console.error('Error getting default AI provider:', error);
        return 'grok';
    }
};

/**
 * Get default model for a provider
 */
export const getDefaultModel = (provider) => {
    const providerConfig = AI_PROVIDERS.find(p => p.id === provider);
    return providerConfig?.defaultModel || 'gemini-2.0-flash';
};

/**
 * Get full AI settings
 */
export const getAISettings = async () => {
    try {
        const globalSettings = await loadGlobalSettings();
        return {
            defaultProvider: globalSettings?.aiSettings?.defaultProvider || 'groq',
            selectionMode: globalSettings?.aiSettings?.selectionMode || 'default'
        };
    } catch (error) {
        console.error('Error getting AI settings:', error);
        return {
            defaultProvider: 'groq',
            selectionMode: 'default'
        };
    }
};

/**
 * Save AI settings (admin only)
 */
export const saveAISettings = async (settings) => {
    try {
        const settingsRef = doc(db, `artifacts/${appId}/settings/global`);
        await setDoc(settingsRef, {
            aiSettings: {
                defaultProvider: settings.defaultProvider || 'groq',
                selectionMode: settings.selectionMode || 'default',
                lastUpdated: new Date().toISOString()
            }
        }, { merge: true });

        // Clear cache to force reload
        clearGlobalSettingsCache();

        return { success: true };
    } catch (error) {
        console.error('Error saving AI settings:', error);
        throw error;
    }
};

/**
 * Clear cache global settings (untuk refresh data)
 */
export const clearGlobalSettingsCache = () => {
    globalSettingsCache = null;
    cacheTimestamp = null;
    console.log('🔄 Global settings cache cleared');
};

/**
 * Get provider configuration for making API calls
 * Returns provider and model to use based on settings
 */
export const getProviderConfig = async () => {
    const settings = await getAISettings();
    const provider = settings.defaultProvider;
    const model = getDefaultModel(provider);

    return {
        provider,
        model,
        selectionMode: settings.selectionMode
    };
};

/**
 * Get random provider from available list
 */
export const getRandomProvider = () => {
    const idx = Math.floor(Math.random() * AI_PROVIDERS.length);
    const provider = AI_PROVIDERS[idx];
    return {
        provider: provider.id,
        model: provider.defaultModel
    };
};

/**
 * Get provider for request based on settings
 */
export const getProviderForRequest = async () => {
    const settings = await getAISettings();

    if (settings.selectionMode === 'random') {
        return getRandomProvider();
    }

    const defaultProvider = settings.defaultProvider || 'grok';
    return {
        provider: defaultProvider,
        model: getDefaultModel(defaultProvider)
    };
};

// ============================================================
// DEPRECATED: These functions are kept for backward compatibility
// API keys are now managed in Cloud Functions
// ============================================================

/**
 * @deprecated API keys are now in Cloud Functions
 * This function always returns cloud as source
 */
export const getApiKeyWithFallback = async (provider) => {
    console.log(`ℹ️ API keys are now managed in Cloud Functions for ${provider}`);
    return { key: null, source: 'cloud' };
};

/**
 * @deprecated Fallback is always enabled as we use Cloud Functions
 */
export const isFallbackEnabled = async () => {
    return true;
};

/**
 * @deprecated API keys are now in Cloud Functions
 */
export const getAllAvailableApiKeys = async () => {
    console.log('ℹ️ API keys are now managed in Cloud Functions');
    return {};
};
